import { Injectable } from '@angular/core';
import { DexieService } from './dexie.service';
import { Observable, Subject } from 'rxjs/Rx';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { ServerResponse } from '../models/serverResponse';
import * as moment from 'moment';
import { DownloadFile } from '../models/download-file';
import { LocalFile } from '../models/local-file';
import { NetworkHelpers } from '../helpers/network-helpers';

declare let cordova;
declare let LocalFileSystem;

@Injectable()

/**
 * (c) it.x informationssysteme gmbh, Alle Rechte vorbehalten.
 *
 * Sync Service.
 */
export class SyncService
{
	/**
	 * Current step.
	 */
	private currentStep: number;

	/**
	 * Steps total.
	 */
	private stepsTotal: number;

	/**
	 * Task queue.
	 */
	private tasks: Array<any>;

	/**
	 * Subject for the current task.
	 */
	private currentTaskSubject = new Subject<any>();

	/**
	 * Current task.
	 */
	public currentTask = this.currentTaskSubject.asObservable();

	/**
	 * Subject for the current progress (percentage).
	 */
	private percentageSubject = new Subject<number>();

	/**
	 * Current progress.
	 */
	public currentPercentage = this.percentageSubject.asObservable();

	/**
	 * Subject for the sync status.
	 */
	private statusSubject = new Subject<any>();

	/**
	 * Sync status.
	 */
	public status = this.statusSubject.asObservable();

	/**
	 * Filelist.
	 */
	private filelist: Array<DownloadFile>;

	/**
	 * Delta: Files to download.
	 */
	private remoteFilesToDownload: Array<any>;

	/**
	 * Delta: Files to delete.
	 */
	private localFilesToRemove: Array<any>;

	/**
	 * Filesystem.
	 */
	private filesystem: any;

	/**
	 * Local files.
	 */
	private localfiles: LocalFile[] = [];

	/**
	 * Debug mode.
	 */
	private debugMode = false;

	/**
	 * Download chunk size in MB
	 */
	private downloadChunkSize = 1;

	/**
	 * Constructor.
	 */
	public constructor(
		private db: DexieService,
		private http: HttpClient,
		private network: NetworkHelpers
	)
	{}

	/**
	 * Start the sync.
	 */
	public startSync()
	{
		if (this.network.isOnline())
		{
			// Emit status.
			this.statusSubject.next('sync');

			// Clear all stores.
			this.clearStores();

			// Execute the queue.
			this.executeQueue();
		}
		else
		{
			this.statusSubject.next('error');
			this.currentTaskSubject.next('SYNC.NO_CONNECTION');
		}
	}

	/**
	 * Clear all stores (IndexedDB).
	 */
	private clearStores()
	{
		const scenesStore = this.db.table('scenes');
		const contentsStore = this.db.table('contents');

		this.db.transaction('rw', [scenesStore, contentsStore], () => {
			scenesStore.clear();
			contentsStore.clear();
		});
	}

	/**
	 * Execute the task queue.
	 */
	private executeQueue()
	{
		this.currentStep = 0;
		this.localfiles = [];

		this.tasks = [
			{execute: 'downloadFilelist', text: 'SYNC.TASKS.DOWNLOAD_FILELIST'},
			{execute: 'determineFileDelta', text: 'SYNC.TASKS.DETERMINE_FILE_DELTA'},
			{execute: 'initFilesystem', text: 'SYNC.TASKS.INIT_FILESYSTEM'},
			{execute: 'removeLocalFiles', text: 'SYNC.TASKS.REMOVE_LOCAL_FILES'},
			{execute: 'downloadRemoteFiles', text: 'SYNC.TASKS.DOWNLOAD_REMOTE_FILES'},
			{execute: 'downloadScenes', text: 'SYNC.TASKS.DOWNLOAD_SCENES'},
			{execute: 'downloadContents', text: 'SYNC.TASKS.DOWNLOAD_CONTENTS'}
		];

		this.stepsTotal = this.tasks.length;
		this.percentageSubject.next(0);
		this.currentTaskSubject.next(this.tasks[0].text);

		// Start execution, but first read out existing localfiles from DB
		this.db.table('localfiles').toArray().then(localfiles => {
			this.localfiles = localfiles || [];
			
			Observable.from(this.tasks).map(task => {
				return Observable.defer(() => {
					return this[task.execute]();
				});
			}).concatAll().subscribe(
				success => this.onTaskSuccess(success),
				error => this.onTaskError(error),
				() => this.onFinished()
			);
		});
	}

	/**
	 * Task: Download scenes (data).
	 */
	private downloadScenes()
	{
		const scenesStore = this.db.table('scenes');
		const api = environment.apiUrl + '/common/ajax.php?bereich=portal&modul_id=101&klasse=city_app_ajax&com=GetScenes';

		// Request data.
		return this.http.get(api, {withCredentials: true}).switchMap((serverResponse: ServerResponse) => {
			if (serverResponse.success === true)
			{
				// Store in database.
				return Observable.from(this.db.transaction('rw', scenesStore, () => {

					for (let i = 0; i < serverResponse.data.length; ++i)
					{
						let scene = serverResponse.data[i];

						// Transform paths.
						if (this.localfiles && this.localfiles.length > 0)
						{
							// Fade images.
							if (scene.background && scene.background !== '')
							{
								for (let file of this.localfiles)
								{
									if (file.id === scene.id && file.file === scene.background)
									{
										this.log('transform scene fade path <' + file.file + '> to <' + file.filepath + '>');
										scene.background = file.filepath;
										break;
									}
								}
							}

							// Switch images.
							if (scene.switch && scene.switch !== '')
							{
								for (let file of this.localfiles)
								{
									if (file.id === scene.id && file.file === scene.switch)
									{
										this.log('transform scene switch path <' + file.file + '> to <' + file.filepath + '>');
										scene.switch = file.filepath;
										break;
									}
								}
							}
						}

						scenesStore.put(scene);
					}
				}).then(() => {
					return true;
				}).catch(error => {
					return Observable.throw(error);
				})).switchMap((value) => {
					return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
				});
			}
			else
			{
				return Observable.throw('Error ' + serverResponse.errno + ' (GetScenes)');
			}
		});
	}

	/**
	 * Task: Download contents (data).
	 */
	private downloadContents()
	{
		const contentsStore = this.db.table('contents');
		const api = environment.apiUrl + '/common/ajax.php?bereich=portal&modul_id=101&klasse=city_app_ajax&com=GetContents';

		// Request data.
		return this.http.get(api, {withCredentials: true}).switchMap((serverResponse: ServerResponse) => {
			if (serverResponse.success === true)
			{
				// Store in database.
				return Observable.from(this.db.transaction('rw', contentsStore, () => {
					for (let i = 0; i < serverResponse.data.length; ++i)
					{
						let content = serverResponse.data[i];

						// Transform paths.
						if (this.localfiles && this.localfiles.length > 0)
						{
							// Tabs.
							if (content.tabs && content.tabs.length > 0)
							{
								for (let j = 0; j < content.tabs.length; j++)
								{
									let tab = content.tabs[j];

									// Slider images.
									if (tab.images && tab.images.length > 0)
									{
										for (let k = 0; k < tab.images.length; k++)
										{
											const image = tab.images[k];

											for (let file of this.localfiles)
											{
												if (file.id === tab.id && file.file === image)
												{
													this.log('transform slider image path <' + file.file + '> to <' + file.filepath + '>');
													tab.images[k] = file.filepath;
													break;
												}
											}
										}
									}

									content.tabs[j] = tab;
								}
							}
						}

						contentsStore.put(content);
					}
				}).then(() => {
					return true;
				}).catch(error => {
					return Observable.throw(error);
				})).switchMap((value) => {
					return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
				});
			}
			else
			{
				return Observable.throw('Error ' + serverResponse.errno + ' (GetServices)');
			}
		});
	}

	/**
	 * Task: Download list of files.
	 */
	private downloadFilelist()
	{
		const api = environment.apiUrl + '/common/ajax.php?bereich=portal&modul_id=101&klasse=city_app_ajax&com=GetSyncData';

		// Request data.
		return this.http.get(api, {withCredentials: true}).switchMap((serverResponse: ServerResponse) => {
			if (serverResponse.success === true)
			{
				this.filelist = serverResponse.data.files;

				this.log('filelist size: ' + this.filelist.length);

				return Observable.of(true);
			}
			else
			{
				return Observable.throw('Error ' + serverResponse.errno + ' (GetSyncData)');
			}
		});
	}

	/**
	 * Task: Determine file delta.
	 */
	private determineFileDelta()
	{
		return Observable.from(this.db.table('localfiles').toArray().then((localfiles: LocalFile[]) => {
			this.log('determine file delta');
			let remoteFilesToDownload = [];
			let localFilesToKeep = [];
			let localFilesToRemove = [];

			// check if the files to download are newer then the local files.
			for (let i = 0; i < this.filelist.length; i++)
			{
				let existsLocal = false;
				let file = this.filelist[i];

				// Skip 0 byte files (invalid)
				if (!file.fileSizeBytes)
				{
					this.log('file with size 0 found: ' + file.file);
					continue;
				}

				for (let k = 0; k < localfiles.length; k++)
				{
					let localFile = localfiles[k];

					if (k !== 1 && localFile.file === file.file)
					{
						localFilesToKeep.push(file);

						if (file.lastChanged <= localFile.lastChanged)
						{
							existsLocal = true;
							break;
						}
					}
				}

				if (!existsLocal)
				{
					// add download url
					file.downloadUrl = environment.apiUrl + '/common/ajax.php?bereich=portal&modul_id=101&klasse=city_app_ajax&com=DownloadFile&offset=-1&name=' + file.file;
					file.filename = this.getLocalName(file);
					file.filepath = this.getLocalDirectory(file.type) + file.filename;

					remoteFilesToDownload.push(file);
				}
			}

			this.log('localFilesToKeep: ' + localFilesToKeep.length);
			this.log('remoteFilesToDownload: ' + remoteFilesToDownload.length);

			for (let i = 0; i < localfiles.length; i++)
			{
				let keepFile = false;
				let currentLocalFile = localfiles[i];

				for (let k = 0; k < localFilesToKeep.length; k++)
				{
					if (currentLocalFile.file === localFilesToKeep[k].file)
					{
						keepFile = true;
						break;
					}
				}

				if (!keepFile)
				{
					localFilesToRemove.push(currentLocalFile);
				}
			}

			this.log('localFilesToRemove: ' + localFilesToRemove.length);

			this.remoteFilesToDownload = remoteFilesToDownload;
			this.localFilesToRemove = localFilesToRemove;

			return true;
		})).switchMap((value) => {
			return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
		});
	}

	/**
	 * Task: Initialize the file system.
	 */
	private initFilesystem()
	{
		if ((<any>window).cordova)
		{
			this.log('cordova is present');

			(<any>window).requestFileSystem  = (<any>window).requestFileSystem || (<any>window).webkitRequestFileSystem;

			let getFilesystem = new Promise((resolve, reject) => {
				(<any>window).requestFileSystem((typeof LocalFileSystem === 'undefined') ? (<any>window).TEMPORARY : LocalFileSystem.PERSISTENT, 0, (filesystem) => {
					this.filesystem = filesystem;
					this.log(((typeof LocalFileSystem === 'undefined') ? 'temporary' : 'persistent') + ' filesystem initalized');

					// create directories.
					let directories = [
						'scenes/',
						'slider/'
					];

					Observable.from(directories).map((directory) => {
						return Observable.defer(() => {
							let createDirectory = new Promise((createSuccess, createError) => {
								(<any>window).resolveLocalFileSystemURL(cordova.file.dataDirectory, (dataDirectoryEntry) => {
									dataDirectoryEntry.getDirectory(directory, {create: true}, (directoryEntry) => {
										this.log('successfully created directory: ' + directoryEntry.toURL());
										createSuccess(directory);
									}, (error) => {
										this.log('error creating directory: ' + error);
										createError(error);
									});
								}, (resolveError) => {
									this.log('error resolving directory: ' + resolveError);
									createError(resolveError);
								});
							});

							return Observable.from(createDirectory).switchMap((value) => {
								return (value === directory ? Observable.of(value) : Observable.throw(value));
							});
						});
					}).concatAll().subscribe(
						directory => this.log('directory ' + directory + ' created/found'),
						error => {
							this.log('could not create or find directory: ' + error, true);
							reject(error);
						},
						() => {
							this.log('all directories are present');
							resolve(true);
						}
					);
				}, (error) => {
					this.log('filesystem could not be initialized: ' + error, true);
					reject(error);
				});
			});

			return Observable.from(getFilesystem).switchMap((value) => {
				return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
			});
		}
		else
		{
			this.log('cordova is not present', true);

			return Observable.of(true);
		}
	}

	/**
	 * Task: Delete local files marked for removal.
	 */
	private removeLocalFiles()
	{
		if (this.filesystem && this.localFilesToRemove && this.localFilesToRemove.length > 0)
		{
			let removeFiles = new Promise((resolve, reject) => {
				Observable.from(this.localFilesToRemove).map((file) => {
					return Observable.defer(() => {
						this.log('removing file ' + file.filepath + ' ...');

						let removeFile = new Promise((removeSuccess, removeError) => {
							(<any>window).resolveLocalFileSystemURL(cordova.file.dataDirectory, (dataDirectoryEntry) => {
								dataDirectoryEntry.getFile(file.filepath, {create: false}, (fileEntry) => {
									fileEntry.remove(() => {
										this.log('file ' + file.filepath + ' successfully removed from filesystem');

										this.db.table('localfiles').delete([file.id, file.file]).then(() => {
											this.log('file [' + file.id + ', ' + file.file + '] successfully removed from database');
											removeSuccess(true);
										}).catch((error) => {
											this.log('file [' + file.id + ', ' + file.file + '] could not been removed from database: ' + error, true);
											removeError(error);
										});
									}, (error) => {
										this.log('file ' + file.filepath + 'could not been removed from filesystem', true);
										this.log(error, true);
										removeError(error);
									});
								}, (error) => {
									this.log('cannot get file from filesystem', true);
									this.log(error, true);
									removeError(error);
								});
							}, (resolveError) => {
								this.log('error resolving directory: ' + resolveError);
								removeError(resolveError);
							});
						});

						return Observable.from(removeFile).switchMap((value) => {
							return (value === true ? Observable.of(value) : Observable.throw(value));
						});
					});
				}).concatAll().subscribe(
					() => {
						this.log('file removed');
					},
					error => {
						this.log(error, true);
						reject(error);
					},
					() => {
						this.log('all files removed');
						resolve(true);
					}
				);
			});

			return Observable.from(removeFiles).switchMap((value) => {
				return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
			});
		}
		else if (environment.expoBuild === true && this.localFilesToRemove && this.localFilesToRemove.length > 0)
		{
			let removeFiles = new Promise((resolve, reject) => {
				Observable.from(this.localFilesToRemove).map((file) => {
					return Observable.defer(() => {
						let removeFile = new Promise((removeSuccess, removeError) => {
							this.db.table('localfiles').delete([file.id, file.file]).then(() => {
								this.log('file [' + file.id + ', ' + file.file + '] successfully removed from database');
								removeSuccess(true);
							}).catch((error) => {
								this.log('file [' + file.id + ', ' + file.file + '] could not been removed from database: ' + error, true);
								removeError(error);
							});
						});

						return Observable.from(removeFile).switchMap((value) => {
							return (value === true ? Observable.of(value) : Observable.throw(value));
						});
					});
				}).concatAll().subscribe(
					() => {
						this.log('file removed');
					},
					error => {
						this.log(error, true);
						reject(error);
					},
					() => {
						this.log('all files removed');
						resolve(true);
					}
				);
			});

			return Observable.from(removeFiles).switchMap((value) => {
				return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
			});
		}
		else
		{
			return Observable.of(true);
		}
	}

	/**
	 * Task: Download remote files from server.
	 */
	private downloadRemoteFiles()
	{
		if (this.filesystem && this.remoteFilesToDownload && this.remoteFilesToDownload.length > 0)
		{
			let downloadFiles = new Promise((resolve, reject) => {
				let downloadedFiles = [];

				Observable.from(this.remoteFilesToDownload).map((file) => {
					return Observable.defer(() => {
						this.log('starting download of [' + file.downloadUrl + ' -> ' + file.filepath + '] ...');

						let downloadFile = new Promise((downloadSuccess, downloadError) => {
							(<any>window).resolveLocalFileSystemURL(cordova.file.dataDirectory, (dataDirectoryEntry) => {
								dataDirectoryEntry.getFile(file.filepath, {create: true, exclusive: false}, (fileEntryCreate) => {
									this.log('file successfully created: ' + fileEntryCreate.toURL());

									// Download file using XHR, since FileTransfer is deprecated
									// see
									// https://cordova.apache.org/blog/2017/10/18/from-filetransfer-to-xhr2.html
									// http://cordova.apache.org/docs/en/latest/reference/cordova-plugin-file/index.html#writeFile
									let oReq = new XMLHttpRequest();
									// Make sure you add the domain name to the Content-Security-Policy <meta> element.
									oReq.open('GET', file.downloadUrl, true);
									// Define how you want the XHR data to come back
									oReq.responseType = 'blob';
									let that = this;
									oReq.onload = function (oEvent) {
										const blob = oReq.response; // Note: not oReq.responseText
										if (blob) {
											fileEntryCreate.createWriter((fileWriter) => {
												that.log('writer created');
												// Handle successful write
												let onWriteEnd = function() {
													that.log('data successfully written to file: ' + fileEntryCreate.toURL());
													downloadedFiles.push(file);
													downloadSuccess(file.filename);
												};
												fileWriter.onerror = function (e) {
													that.log('failed to write to file: ' + e.toString(), true);
												};
												that.log('start writing');

												// Workaround for crashing large file writes using chunking
												// see https://stackoverflow.com/questions/18736255/using-phonegap-filewriter-write-for-big-files
												let written = 0;
												const BLOCK_SIZE = that.downloadChunkSize * 1024 * 1024; // write X MB every time of write
												function writeNext(cbFinish) {
													fileWriter.onwrite = function(evt) {
														if (written < blob.size)
														{
															writeNext(cbFinish);
														}
														else
														{
															cbFinish();
														}
													};
													if (written) {
														fileWriter.seek(fileWriter.length);
													}
													fileWriter.write(blob.slice(written, written + Math.min(BLOCK_SIZE, blob.size - written)));
													written += Math.min(BLOCK_SIZE, blob.size - written);
												}
												writeNext(onWriteEnd);
											});
										} else {
											that.log('invalid XHR response', true);
										}
									};
									oReq.send(null);
								}, (fileError) => {
									this.log('could not create file in filesystem: ' + fileError, true);
									downloadError(fileError);
								});
							}, (resolveError) => {
								this.log('error resolving directory: ' + resolveError);
								downloadError(resolveError);
							});
						});

						return Observable.from(downloadFile).switchMap((value) => {
							return (value === file.filename ? Observable.of(value) : Observable.throw(value));
						});
					});
				}).concatAll().subscribe(
					() => {
						this.log('file downloaded');
					},
					error => {
						this.log(error, true);

						const localfilesStore = this.db.table('localfiles');

						this.db.transaction('rw', localfilesStore, () => {
							for (let i = 0; i < downloadedFiles.length; ++i)
							{
								localfilesStore.put(downloadedFiles[i]);
							}
						}).then(() => {
							this.log('saved downloaded files in database - queue canceled due to error', true);
							reject(error);
						}).catch(() => {
							reject(error);
						});
					},
					() => {
						this.log('all downloads completed');

						const localfilesStore = this.db.table('localfiles');

						this.db.transaction('rw', localfilesStore, () => {
							for (let i = 0; i < downloadedFiles.length; ++i)
							{
								localfilesStore.put(downloadedFiles[i]);
							}
						}).then(() => {
							this.log('saved downloaded files in database');

							this.db.table('localfiles').toArray().then(localfiles => {
								this.localfiles = localfiles;
								resolve(true);
							});
						}).catch(error => {
							reject(error);
						});
					}
				);
			});

			return Observable.from(downloadFiles).switchMap((value) => {
				return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
			});
		}
		else if (environment.expoBuild === true && this.remoteFilesToDownload && this.remoteFilesToDownload.length > 0)
		{
			let downloadFiles = new Promise((resolve, reject) => {
				let downloadedFiles = [];

				Observable.from(this.remoteFilesToDownload).map((file) => {
					return Observable.defer(() => {
						this.log('starting image data download of [' + file.file + '] ...');

						let downloadFile = new Promise((downloadSuccess, downloadError) => {
							const api = environment.apiUrl + '/common/ajax.php?bereich=portal&modul_id=101&klasse=city_app_ajax&com=DownloadImageData';
							const options = {
								withCredentials: true,
								params: new HttpParams().set('name', file.file)
							};

							// Request data.
							this.http.get(api, options).subscribe((serverResponse: ServerResponse) => {
								if (serverResponse.success === true)
								{
									file.filepath = serverResponse.data['imageBase64'];
									downloadedFiles.push(file);

									downloadSuccess(file.filename);
									downloadSuccess(true);
								}
								else
								{
									this.log('could not download file: ' + serverResponse.message, true);
									downloadError(serverResponse.message);
								}
							});
						});

						return Observable.from(downloadFile).switchMap((value) => {
							return (value === file.filename ? Observable.of(value) : Observable.throw(value));
						});
					});
				}).concatAll().subscribe(
					() => {
						this.log('file downloaded');
					},
					error => {
						this.log(error, true);

						const localfilesStore = this.db.table('localfiles');

						this.db.transaction('rw', localfilesStore, () => {
							for (let i = 0; i < downloadedFiles.length; ++i)
							{
								localfilesStore.put(downloadedFiles[i]);
							}
						}).then(() => {
							this.log('saved downloaded files in database - queue canceled due to error', true);
							reject(error);
						}).catch(() => {
							reject(error);
						});
					},
					() => {
						this.log('all downloads completed');

						const localfilesStore = this.db.table('localfiles');

						this.db.transaction('rw', localfilesStore, () => {
							for (let i = 0; i < downloadedFiles.length; ++i)
							{
								localfilesStore.put(downloadedFiles[i]);
							}
						}).then(() => {
							this.log('saved downloaded files in database');

							this.db.table('localfiles').toArray().then(localfiles => {
								this.localfiles = localfiles;
								resolve(true);
							});
						}).catch(error => {
							reject(error);
						});
					}
				);
			});

			return Observable.from(downloadFiles).switchMap((value) => {
				return (value === true ? Observable.of(value) : Observable.throw('Error: ' + value));
			});
		}
		else
		{
			return Observable.of(true);
		}
	}

	/**
	 * Eventhandler: Task successfull.
	 */
	private onTaskSuccess(success)
	{
		++this.currentStep;

		// Calculate percentage.
		const percentage = (this.currentStep * 100) / this.stepsTotal;
		this.percentageSubject.next(percentage);

		// Emit next task text.
		if (this.currentStep < this.tasks.length)
		{
			this.currentTaskSubject.next(this.tasks[this.currentStep].text);
		}
	}

	/**
	 * Eventhandler: Task failed.
	 */
	private onTaskError(error)
	{
		// Emit status.
		this.statusSubject.next('error');

		// Emit messsage.
		this.currentTaskSubject.next(error);
	}

	/**
	 * Eventhandler: Queue finished.
	 */
	private onFinished()
	{
		// Update local storage.
		localStorage.setItem('SYNC_COMPLETE', '1');
		localStorage.setItem('LAST_UPDATE', this.now());

		let firstSync = localStorage.getItem('FIRST_SYNC');

		if (firstSync)
		{
			if (firstSync === '1')
			{
				localStorage.setItem('FIRST_SYNC', '0');
			}
		}
		else
		{
			localStorage.setItem('FIRST_SYNC', '1');
		}

		// Emit status.
		this.statusSubject.next('success');

		// Emit message.
		this.currentTaskSubject.next('SYNC.SUCCESS');
	}

	/**
	 * Get the current datetime.
	 */
	private now(): string
	{
		return moment().format('YYYY-MM-DD HH:mm:ss');
	}

	/**
	 * Log a message to the console.
	 */
	private log(message: any, error = false)
	{
		if (this.debugMode === true)
		{
			if (error === false)
			{
				console.log(message);
			}
			else
			{
				console.error(message);
			}
		}
	}

	/**
	 * Determines the filename for the local file system.
	 */
	private getLocalName(file: any)
	{
		let filename = file.id + '.' + file.extension;

		switch (file.type)
		{
			case 'slider':
				filename = file.id + '_' + file.name + '.' + file.extension;
				break;
		}

		return filename;
	}

	/**
	 * Get directory according to the file type.
	 */
	private getLocalDirectory(type: string)
	{
		let directory = 'documents/';

		switch (type)
		{
			case 'scene':
				directory = 'scenes/';
				break;

			case 'slider':
				directory = 'slider/';
				break;
		}

		return directory;
	}
}



// WEBPACK FOOTER //
// /var/data/workspace/hess/hess_smart_city_web/src/app/services/sync.service.ts